
import SketchViewModel from "@arcgis/core/widgets/Sketch/SketchViewModel";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import Polygon from "@arcgis/core/geometry/Polygon";
import Point from "@arcgis/core/geometry/Point";
import Polyline from "@arcgis/core/geometry/Polyline";
import Graphic from "@arcgis/core/Graphic";
import PropTypes from 'prop-types';
import { emitCustomEvent, useCustomEventListener } from 'react-custom-events';
import Events from '../../defaults/Events';
import {useEffect, useRef, useState} from "react";
import * as geometryEngine from "@arcgis/core/geometry/geometryEngine";
import * as projection from "@arcgis/core/geometry/projection";
import SpatialReference from "@arcgis/core/geometry/SpatialReference";
import symbols from '../../defaults/Symbols.json';
import SimpleMarkerSymbol from '@arcgis/core/symbols/SimpleMarkerSymbol';
import {getPolygonSymbol} from "./MapEventHelper";
import FieldNames from "../../defaults/FieldNames.json";

function DrawToolbar({view, modifiedGraphicsLayer}) {

  const [sketchViewModel, setSketchViewModel] = useState(null);
  let isMovingPoint = false;
  let isUploading = false;
  let shouldUpdateGraphic = true;
  const sketchGraphicsLayer = useRef(null)
  //
  // useEffect(() => {
  //   //setup initial graphics layer
  //   setSketchGraphicsLayer();
  // }, []);

  //Setup sketchviewmodel once we have a view and a graphicslayer
  useEffect(() => {
    if (!view || !sketchGraphicsLayer)
    {
      return;
    }

    if(sketchViewModel)
    {
      return;
    }
    createSketchViewModel(view);
  }, [view, sketchGraphicsLayer, sketchViewModel]);

  const createSketchViewModel = (view) => {
    const gl = new GraphicsLayer({
      view: view,
      listMode: 'hide',
      title: 'sketch-graphics-layer'
    })
      sketchGraphicsLayer.current = gl;
    setSketchViewModel(new SketchViewModel({
      view: view,
      layer: gl,
      updateOnGraphicClick: true,
      defaultUpdateOptions:
      {
        toggleToolOnClick: false
      }
    }));
  }


  // useEffect(() => {
  //   if (!view || !sketchGraphicsLayer) {
  //       return;
  //   }
  //   if(sketchViewModel){
  //     return;
  //   }

  //   setSketchViewModel(new SketchViewModel({
  //       view: view,
  //       layer: sketchGraphicsLayer,
  //       updateOnGraphicClick: true,
  //       defaultUpdateOptions:
  //       {
  //           toggleToolOnClick: false
  //       }
  //   }));

  // }, [view, sketchGraphicsLayer, sketchViewModel]);


  useCustomEventListener(Events.DisableDrawMode, () => {
    sketchViewModel.cancel();
    sketchViewModel.layer.graphics.removeAll();
    emitCustomEvent(Events.ClearModifiedLayer);
  });

  useCustomEventListener(Events.CompleteDrawMode, () => {
    sketchViewModel.complete();
    onGraphicUpdateComplete();
  });

  //Draw a polygon when someone tells us to.
  useCustomEventListener(Events.EnableDrawMode, data => {
    if(data && data.attributes && data.attributes["IsPoint"] === FieldNames.yesNo.yes){
      //Do nothing here as this is a point

    } else {
      view.popup.visible = false;
      if (data && data.geometry) {
        const perimeter = new Polygon({
          rings: data.geometry.rings,
          spatialReference: new SpatialReference( {
            wkid: 4269
          })
        });

        return projection.load().then(function() {
          const mercatorGeometry = projection.project(perimeter, new SpatialReference({wkid: 102100 }));
          let defaultSymbol = {
                type: "simple-fill",
                color: "rgba(138,43,226, 0.8)",
                style: "solid",
                outline: {
                    color: "black",
                    width: 1
                }
            }
          let symbol = data.attributes && data.attributes[FieldNames.featureFields.class] ?  getPolygonSymbol(data.attributes[FieldNames.featureFields.class] ) : defaultSymbol;
          symbol.outline = {
            style: "long-dash-dot",
            color: "cyan",
            width: 3
          }
          const polyGraphic = new Graphic({
            geometry: mercatorGeometry,
            symbol: symbol
          });

          sketchGraphicsLayer.current.add(polyGraphic);

          sketchViewModel.on(["update"], onGraphicUpdate);
          sketchViewModel.update(polyGraphic, { tool: "reshape" });
          if(data.callback){
            data.callback(polyGraphic, data.callbackParams);
          }
          return polyGraphic;
        })

      }
      else {
        sketchViewModel.create('polygon');
        sketchViewModel.on('create', (args) => {
            if (args.state === "complete" && args.graphic && args.graphic.geometry) {
                data.callback(args.graphic.geometry, data.callbackParams);
                disableDrawMode();
            }
        });
      }
    }
  });

  useCustomEventListener(Events.ToggleIsMovingPoint, data => {
    isMovingPoint = data;
  });

  useCustomEventListener(Events.GetIsMovingPoint, (done) => {
    done(isMovingPoint);
  });

  useCustomEventListener(Events.ToggleShouldUpdateGraphic, data => {
    shouldUpdateGraphic = data;
  });

  useCustomEventListener(Events.GetShouldUpdateGraphic, (done) => {
    done(shouldUpdateGraphic);
  });

  useCustomEventListener(Events.ClearSketchGraphicsLayer, () => {
    sketchGraphicsLayer.current.graphics.removeAll();
  })

  useCustomEventListener(Events.EnablePointMode, data => {
    view.popup.visible = false;
    sketchViewModel.create('point');
    sketchViewModel.on('create', (args) => {
      if (args.state === "complete" && args.graphic && args.graphic.geometry) {
        if(isMovingPoint){
          //const ptBuff = geometryEngine.geodesicBuffer(args.graphic.geometry, "0.05", "feet");
          const ptBuff = geometryEngine.geodesicBuffer(args.graphic.geometry, "7000", "feet");
          const polyGraphic = new Graphic({
            geometry: Polygon.fromJSON(ptBuff.toJSON()),
            symbol: {
              type: "simple-fill",
              color: "rgba(138,43,226, 0.8)",
              style: "solid",
              outline: {
                  color: "black",
                  width: 1
              }
            }
          });
          sketchGraphicsLayer.current.add(polyGraphic);
          emitCustomEvent(Events.ModifiedGraphicChanged, polyGraphic);
          data.callback(ptBuff, data.callbackParams);
          disableDrawMode();
        } else {
          if (args.state === "complete" && args.graphic && args.graphic.geometry) {
            //const ptBuff = geometryEngine.geodesicBuffer(args.graphic.geometry, "0.05", "feet");
            const ptBuff = geometryEngine.geodesicBuffer(args.graphic.geometry, "7000", "feet");
            data.callback(ptBuff, data.callbackParams);
            disableDrawMode();
          }
        }
      }
    });
  });

  useCustomEventListener(Events.EnablePostFireLineMode, data => {
    view.popup.visible = false;
      if (data && data.geometry) {
        const polyline = new Polyline(data.geometry);
        projection.load().then(function() {
          const mercatorGeometry = projection.project(polyline, new SpatialReference({wkid: 102100 }));
          const lineGraphic = new Graphic({
            geometry: mercatorGeometry,
            symbol: symbols.lineSymbolMod,
          });

          sketchGraphicsLayer.current.add(lineGraphic);

          sketchViewModel.on(["update"], onLineGraphicUpdate);
          sketchViewModel.update([lineGraphic], { tool: "reshape" });
          if(data.callback){
            data.callback(lineGraphic, data.callbackParams);
          }
        })
          //const mercatorGeometry = webMercatorUtils.geographicToWebMercator(geometryJsonUtils.fromJSON(data.geometry));

      }
      else {
        sketchViewModel.create('polyline');
        sketchViewModel.on('create', (args) => {
            if (args.state === "complete" && args.graphic && args.graphic.geometry) {
                data.callback(args.graphic.geometry, data.callbackParams);
                disableDrawMode();
            }
        });
      }
  })

  useCustomEventListener(Events.EnablePostFirePointMode, data => {
    view.popup.visible = false;
    sketchViewModel.create('point');
    sketchViewModel.on('create', (args) => {
      if (args.state === "complete" && args.graphic && args.graphic.geometry) {
        //if(isMovingPoint){
          const point = new Point(args.graphic.geometry);
          const pointGraphic = new Graphic({
            geometry: point,
            symbol: symbols.pointSymbolMod,
          });

          sketchGraphicsLayer.current.add(pointGraphic);
          emitCustomEvent(Events.ModifiedGraphicPostFirePointChanged, pointGraphic);
          data.callback(point, data.callbackParams);
          disableDrawMode();
      }
    });
  });

  useCustomEventListener(Events.UploadGeometry, () => {
    isUploading = true;
  });

  const onGraphicUpdate = (event) => {
    const graphic = event.graphics[0];
    if(!isUploading){
      if (shouldUpdateGraphic)
      {
        emitCustomEvent(Events.ModifiedGraphicChanged, graphic);
      }
    }
  };

  const onLineGraphicUpdate = (event) => {
    const graphic = event.graphics[0];
    if(!isUploading){
      if (shouldUpdateGraphic)
      {
        emitCustomEvent(Events.ModifiedGraphicLineChanged, graphic);
      }
    }
  };

  const onGraphicUpdateComplete = () => {
    if(isUploading){
      isUploading = false;
    }
    sketchViewModel.complete();
    let sketchGraphic = sketchGraphicsLayer.current.graphics.getItemAt(0)
    if(sketchGraphic){
        modifiedGraphicsLayer.graphics.removeAll();
        emitCustomEvent(Events.ModifiedGraphicChanged, sketchGraphicsLayer.current.graphics.getItemAt(0));
        sketchGraphicsLayer.current.graphics.removeAll();
    }
  }

  const disableDrawMode = () => {
    shouldUpdateGraphic = false;
    sketchViewModel.cancel();
    sketchViewModel.layer.graphics.removeAll();
    sketchViewModel.destroy();
    setSketchViewModel(null);
  }

  return null;
}

DrawToolbar.propTypes = {
  view: PropTypes.any,
}

export default DrawToolbar;
